import { type ReactNode } from 'react'

import { Box, Stack, Typography } from '@mui/material'

import loginIllustration from '~/assets/login-new.png'
import logo from '~/assets/ISOLOGO-03.svg'
import PlatformLanguageSelector from '~/components/LanguageSelector'

interface LoginLayoutProps {
  children: ReactNode
  environment: string
  environmentHref: string
  switchLabel: string
  title: string
}

/**
 * Visual shell for the authentication flows.
 *
 * The form is intentionally injected as children so password recovery,
 * password reset and Google SSO keep the same behavior while the layout stays
 * reusable and focused on presentation.
 */
export default function LoginLayout({
  children,
  environment,
  environmentHref,
  switchLabel,
  title,
}: LoginLayoutProps) {
  return (
    <Stack
      alignItems='center'
      minHeight='100svh'
      justifyContent='center'
      sx={{
        px: { xs: 0, sm: 3 },
        py: { xs: 0, sm: 4 },
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        backgroundColor: '#f8faf7',
        backgroundImage:
          'radial-gradient(circle at 0% 100%, rgba(224, 240, 217, 0.8), transparent 42%)',
      }}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 1200 900"
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <defs>
          <pattern
            id="login-background-pattern"
            width="24"
            height="24"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="12" cy="12" r="2" fill="#50aa47" opacity="0.12" />
          </pattern>
        </defs>

        <rect width="1200" height="900" fill="url(#login-background-pattern)" />
      </svg>

      <Box
        sx={{
          width: '100%',
          maxWidth: 840,
          minHeight: { xs: '100svh', sm: 640 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          overflow: 'hidden',
          borderRadius: { xs: 0, sm: 5 },
          backgroundColor: '#fff',
          boxShadow: {
            xs: 'none',
            sm: '0 22px 60px rgba(34, 55, 39, 0.12)',
          },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack
          component="section"
          aria-labelledby="login-title"
          sx={{
            minWidth: 0,
            justifyContent: { xs: 'center', md: 'initial' },
            px: { xs: 2.5, sm: 4.5, lg: 5.5 },
            py: { xs: 3, sm: 3.5 },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: { xs: 2.5, sm: 1.5 } }}
          >
            <Box
              component="img"
              src={logo}
              alt="Stoplight"
              sx={{
                display: { xs: 'block', md: 'none' },
                width: 42,
                height: 42,
                objectFit: 'contain',
              }}
            />
            <Box sx={{ display: 'block', ml: 'auto' }}>
              <PlatformLanguageSelector />
            </Box>
          </Stack>

          <Box
            sx={{
              width: '100%',
              maxWidth: 430,
              mx: 'auto',
              flex: { xs: 'unset', md: 1 },
            }}
          >
            <Typography
              id="login-title"
              variant='h4'
              fontSize={30}
              fontWeight={700}
              lineHeight='38px'
              letterSpacing='0px'
              sx={{ color: '#4d5351' }}
            >
              {title}
            </Typography>

            <Typography
              component="p"
              variant="h6"
              fontSize={17}
              fontWeight={400}
              lineHeight={1}
              letterSpacing="-0.06px"
              sx={{ mt: 1.25, mb: 3.25, color: '#7b817f' }}
            >
              {switchLabel}{' '}
              <Box
                component="a"
                href={environmentHref}
                sx={{
                  color: '#50aa47',
                  fontWeight: 700,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {environment}
              </Box>
            </Typography>

            {children}
          </Box>

        </Stack>

        <Stack
          component="aside"
          alignItems="center"
          justifyContent="center"
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'relative',
            overflow: 'hidden',
            minHeight: 620,
            backgroundColor: '#fdf5ea',
          }}
        >
          <Box
            component="img"
            src={loginIllustration}
            alt="Familia y mascota usando la plataforma del Semáforo"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              display: 'block',
            }}
          />
        </Stack>
      </Box>
    </Stack>
  )
}
